(() => {
  'use strict';

  const doc = document;
  const body = doc.body;
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const qsa = (selector, scope = doc) => Array.from(scope.querySelectorAll(selector));
  const qs = (selector, scope = doc) => scope.querySelector(selector);

  function initAnnouncementBars(scope = doc) {
    qsa('[data-announcement]', scope).forEach((bar) => {
      const close = qs('[data-announcement-close]', bar);
      if (!close) return;
      close.addEventListener('click', () => {
        bar.hidden = true;
        setAnnouncementHeight();
      });
    });
    setAnnouncementHeight();
  }

  function setAnnouncementHeight() {
    const bar = qs('[data-announcement]:not([hidden])');
    doc.documentElement.style.setProperty('--announcement-height', `${bar ? bar.offsetHeight : 0}px`);
  }

  function initHeader(scope = doc) {
    qsa('[data-site-header]', scope).forEach((header) => {
      const overlay = header.classList.contains('is-overlay');
      if (!overlay) return;
      const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 24);
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
    });
  }

  function initMobileDrawer(scope = doc) {
    const drawer = qs('[data-mobile-drawer]', scope);
    if (!drawer) return;
    const openers = qsa('[data-drawer-open]', scope);
    const closers = qsa('[data-drawer-close]', scope);
    const overlay = qs('[data-screen-overlay]');
    let previousFocus = null;

    const focusableSelector = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

    const open = () => {
      previousFocus = doc.activeElement;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      body.classList.add('drawer-open');
      if (overlay) overlay.hidden = false;
      const first = qs(focusableSelector, drawer);
      if (first) first.focus();
    };

    const close = () => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      body.classList.remove('drawer-open');
      if (overlay) overlay.hidden = true;
      if (previousFocus) previousFocus.focus();
    };

    openers.forEach((button) => button.addEventListener('click', open));
    closers.forEach((button) => button.addEventListener('click', close));
    if (overlay) overlay.addEventListener('click', close);
    drawer.addEventListener('click', (event) => {
      if (event.target.closest('a')) close();
    });
    doc.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && drawer.classList.contains('is-open')) close();
      if (event.key !== 'Tab' || !drawer.classList.contains('is-open')) return;
      const focusable = qsa(focusableSelector, drawer).filter((el) => el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && doc.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && doc.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  function initReveal(scope = doc) {
    const items = qsa('[data-reveal]', scope);
    if (!items.length) return;
    if (prefersReducedMotion || body.dataset.enableMotion !== 'true' || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    items.forEach((item) => observer.observe(item));
  }

  function initOrigins(scope = doc) {
    qsa('[data-origins]', scope).forEach((section) => {
      const items = qsa('[data-origin-item]', section);
      const media = qsa('[data-origin-media]', section);
      if (!items.length || !media.length || !('IntersectionObserver' in window)) return;
      const activate = (id) => {
        media.forEach((node) => node.classList.toggle('is-active', node.dataset.originMedia === id));
        items.forEach((node) => node.classList.toggle('is-active', node.dataset.originItem === id));
      };
      activate(items[0].dataset.originItem);
      const observer = new IntersectionObserver((entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) activate(visible.target.dataset.originItem);
      }, { rootMargin: '-30% 0px -45% 0px', threshold: [0, .2, .5, .8] });
      items.forEach((item) => observer.observe(item));
    });
  }

  function initGalleries(scope = doc) {
    qsa('[data-product-gallery]', scope).forEach((gallery) => {
      const slides = qsa('[data-gallery-slide]', gallery);
      const thumbs = qsa('[data-gallery-thumb]', gallery);
      if (!slides.length) return;
      const activate = (id) => {
        slides.forEach((slide) => slide.classList.toggle('is-active', slide.dataset.gallerySlide === id));
        thumbs.forEach((thumb) => {
          const active = thumb.dataset.galleryThumb === id;
          thumb.classList.toggle('is-active', active);
          thumb.setAttribute('aria-current', active ? 'true' : 'false');
        });
      };
      thumbs.forEach((thumb) => thumb.addEventListener('click', () => activate(thumb.dataset.galleryThumb)));
      activate(slides[0].dataset.gallerySlide);
    });
  }

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = Number(cents || 0);
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const defaultFormat = '${{amount}}';
    const targetFormat = format || defaultFormat;
    const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      if (Number.isNaN(number) || number == null) return '0';
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? `${dollars}${decimal}${parts[1]}` : dollars;
    };
    const match = targetFormat.match(placeholderRegex);
    if (!match) return formatWithDelimiters(value);
    let formatted;
    switch (match[1]) {
      case 'amount_no_decimals': formatted = formatWithDelimiters(value, 0); break;
      case 'amount_with_comma_separator': formatted = formatWithDelimiters(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': formatted = formatWithDelimiters(value, 0, '.', ','); break;
      default: formatted = formatWithDelimiters(value, 2); break;
    }
    return targetFormat.replace(placeholderRegex, formatted);
  }

  function initProductForms(scope = doc) {
    qsa('[data-product-root]', scope).forEach((root) => {
      const variantSelect = qs('[data-variant-select]', root);
      const json = qs('[data-variant-json]', root);
      const price = qs('[data-product-price]', root);
      const submit = qs('[data-product-submit]', root);
      if (!variantSelect || !json) return;
      let variants = [];
      try { variants = JSON.parse(json.textContent); } catch (error) { return; }
      const moneyFormat = root.dataset.moneyFormat || '${{amount}}';
      const update = () => {
        const variant = variants.find((item) => String(item.id) === String(variantSelect.value));
        if (!variant) return;
        if (price) price.textContent = formatMoney(variant.price, moneyFormat);
        if (submit) {
          submit.disabled = !variant.available;
          submit.querySelector('[data-submit-label]').textContent = variant.available ? submit.dataset.availableLabel : submit.dataset.soldOutLabel;
        }
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      };
      variantSelect.addEventListener('change', update);
      update();
    });
  }

  function initWaitlistCountryTags(scope = doc) {
    qsa('[data-waitlist-form]', scope).forEach((form) => {
      const select = qs('[data-country-select]', form);
      const tags = qs('[data-waitlist-tags]', form);
      if (!select || !tags) return;
      const baseTags = tags.dataset.baseTags || 'xli23,prologue-waitlist';
      const update = () => {
        const slug = select.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        tags.value = slug ? `${baseTags},country-${slug}` : baseTags;
      };
      select.addEventListener('change', update);
      update();
    });
  }

  function initStickyProductAction(scope = doc) {
    qsa('[data-sticky-product-action]', scope).forEach((sticky) => {
      const primary = qs('[data-primary-product-action]');
      if (!primary || !('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => sticky.classList.toggle('is-visible', !entry.isIntersecting));
      }, { threshold: 0 });
      observer.observe(primary);
    });
  }

  function initDetails(scope = doc) {
    qsa('details[data-exclusive]', scope).forEach((detail) => {
      detail.addEventListener('toggle', () => {
        if (!detail.open) return;
        qsa(`details[data-exclusive="${detail.dataset.exclusive}"]`, detail.parentElement).forEach((other) => {
          if (other !== detail) other.open = false;
        });
      });
    });
  }

  function initAll(scope = doc) {
    initAnnouncementBars(scope);
    initHeader(scope);
    initMobileDrawer(scope);
    initReveal(scope);
    initOrigins(scope);
    initGalleries(scope);
    initProductForms(scope);
    initWaitlistCountryTags(scope);
    initStickyProductAction(scope);
    initDetails(scope);
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();

  doc.addEventListener('shopify:section:load', (event) => initAll(event.target));
  window.addEventListener('resize', setAnnouncementHeight);
})();
